import React, { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  addMonths,
  format,
  formatISO,
  parseISO,
  startOfDay,
  startOfMonth,
  subMonths,
} from "date-fns";

// Form Elements & Components
import {
  CheckBox,
  DayPicker,
  DoctorCard,
  Heading,
  HourPicker,
  MonthPicker,
  Submit,
} from "../FormElements";

// Helpers
import { RootDataType } from "../Helpers/interfaces";
import { FieldStatus } from "../Helpers/FieldStatus";
import { clear, onSubmit, setStorage } from "../Helpers/formFunctions";

import {
  FormDispatch,
  FormFields,
  FormReducer,
  FormStateType,
} from "../Helpers/formTypes";

// Hooks
import { ErrorMessage } from "../NoAppointments/ErrorMessage";
import { useLabels } from "../Hooks/useLabels";
import { LoadingAnimation } from "../../LoadingAnimation/LoadingAnimation";
import { LocationSelect } from "../FormElements/Select/LocationSelect";
import { DoctorSelect } from "../FormElements/Select/DoctorSelect";
import { useFilteredLocations } from "../Hooks/useFilteredLocations";
import { useFilteredDoctors } from "../Hooks/useFilteredDoctors";
import { useInterval } from "../Hooks/useAppointments";
import { useFilteredAppointmentsDetails } from "../Hooks/useAppointmentDetails";
import { AppointmentDto } from "../../../../Api/Model/AppointmentDto.csharp";
import { useMissingAppointmentsMessage } from "../Hooks/useNoAppointmentsMessage";
import { DoctorDto } from "../../../../Api/Model/DoctorDto.csharp";
import { LocationDto } from "../../../../Api/Model/LocationDto.csharp";
import { RemoteContactViewModel } from "../../../../Api/Model/RemoteContactViewModel.csharp";
import { usePricingSummary } from "../Hooks/usePricingSummary";
import { GtmService } from "../GtmService";
import { GenericModal } from "../FormElements/PopUps/GenericModal";
import { useTimeZoneModal } from "../Hooks/useTimeZoneModal";
import { useCancellationDeadline } from "../Hooks/useCancellationDeadline";
import { nb } from "date-fns/locale";
import { useMissingAppointments } from "../Hooks/useMissingAppointments";
import {
  findRelatedServiceLocationSetup,
  isRelatedServiceEnabled,
} from "../Helpers/relatedServiceHelper";
import { newDateInOsloTimeZone } from "../Helpers/timeZoneHelpers";
import { AppointmentBookingSystem } from "@apiModels/AppointmentBookingSystem.csharp";
import { HelsebokaInfo } from "@apiModels/HelsebokaInfo.csharp";
import { RichTextArea } from "../RichTextArea/RichTextArea";

type SecondStepType = {
  state: FormStateType;
  dispatch: FormDispatch;
  nextStep: () => void;
  rootData: RootDataType;
};

export type IntervalDay = {
  hasAppointments: boolean;
  date: Date;
};

export type HelsebokaFilter = {
  defragment: boolean;
  treatmentCategoryIds: number[];
  treatmentIds: number[];
  type: "NORMAL" | "VIDEO";
};

export const SecondStep: FC<SecondStepType> = ({
  state,
  dispatch,
  nextStep,
  rootData,
}) => {
  const { filteredLocations, unavailableLocations } = useFilteredLocations(
    rootData.centers,
    state.specialization,
    state.service,
    state.serviceGroup
  );
  const { filteredDoctors } = useFilteredDoctors(
    rootData.doctors,
    state.service,
    state.location
  );

  const [isHelsebokaModalOpen, setIsHelsebokaModalOpen] = useState(false);

  const getHelsebokaFilterValue = (
    helsebokaInfo: HelsebokaInfo
  ): HelsebokaFilter => {
    return {
      defragment: true,
      treatmentIds: [helsebokaInfo.serviceId],
      treatmentCategoryIds: [helsebokaInfo.serviceCategoryId],
      type: "NORMAL",
    };
  };

  const submitHelsebokaModal = () => {
    setIsHelsebokaModalOpen(false);

    if (!!state.appointment.helsebokaInfo) {
      const filter = getHelsebokaFilterValue(state.appointment.helsebokaInfo);
      const uri = encodeURI(
        `https://www.helseboka.app/booking/clinic/${
          state.appointment.helsebokaInfo.clinicId
        }/treatment?timeSlotId=${
          state.appointment.helsebokaInfo.appointmentId
        }&filter=${JSON.stringify(filter)}`
      );
      window.open(uri, "_blank", "noreferrer");
    }
  };

  const changeShowRelatedSetup = (value: boolean, relatedServiceId: number) => {
    updateMonthToCurrentMonth();
    updateDayToFirstAvailable();

    const newState = new Map<number, boolean>(state.showRelatedServicesMap);

    newState.set(relatedServiceId, value);

    dispatch({
      type: FormReducer.update,
      field: FormFields.showRelatedServicesMap,
      payload: newState,
    });
    setValue(`${FormFields.showRelatedServices}-${relatedServiceId}`, value);
  };

  useMissingAppointments(state.service, state.showRelatedServicesMap, dispatch);

  const [selectedMonth, setSelectedMonth] = useState<Date>(
    startOfMonth(newDateInOsloTimeZone())
  );
  const [selectedDay, setSelectedDay] = useState<Date>(
    startOfDay(newDateInOsloTimeZone())
  );

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [autoQueryRepeat, setAutoQueryRepeat] = useState(0);

  const { bookingLabels } = useLabels();

  const { secondStepLabels } = bookingLabels!.stepsBookingLabels!;
  const commonLabels = bookingLabels!.commonLabels;

  const { missingAppointmentsMessage: noAppointmentMessage } =
    useMissingAppointmentsMessage(
      state.doctor,
      state.location,
      state.specialization,
      state.service,
      state.missingAppointments,
      rootData.centers
    );

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    register,
  } = useForm<FormStateType>({
    defaultValues: state,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    !!state.selectedMonth
      ? handleMonthChange(parseISO(state.selectedMonth))
      : handleMonthChange(startOfMonth(newDateInOsloTimeZone()));

    !!state.selectedDay
      ? handleDayChange(parseISO(state.selectedDay))
      : handleDayChange(startOfDay(newDateInOsloTimeZone()));
  }, []);

  useEffect(() => {
    for (const rs of state.relatedServiceSetups) {
      const relatedServiceLocationSetup = findRelatedServiceLocationSetup(
        rs,
        state.location
      );

      changeShowRelatedSetup(
        relatedServiceLocationSetup?.showRelatedService ??
          rs.showRelatedService,
        rs.relatedServiceId
      );
    }
  }, [state.location]);

  const updateRemoteContact = (remoteContact: RemoteContactViewModel) => {
    dispatch({
      type: FormReducer.update,
      field: FormFields.remoteContact,
      payload: remoteContact,
    });
  };

  const {
    interval,
    filteredAppointments,
    loadingAppointments,
    error: appointmentsError,
    warning: appointmentsWarning,
    failedCenters: appointmentsFailedCenters,
  } = useInterval(
    state,
    selectedMonth,
    autoQueryNextMonth,
    autoQueryRepeat,
    filteredDoctors
  );

  useEffect(() => {
    if (state.appointment.serviceVariantId) {
      setValue(FormFields.appointment, state.appointment);
      dispatch({
        type: FormReducer.update,
        field: FormFields.appointment,
        payload: state.appointment,
      });
    }
  }, []);

  const {
    filteredAppointmentsDetails,
    loadingAppointmentsDetails,
    error: appointmentsDetailsError,
    warning: appointmentsDetailsWarning,
    failedCenters: appointmentsDetailsFailedCenters,
  } = useFilteredAppointmentsDetails(
    state,
    updateRemoteContact,
    selectedDay,
    filteredDoctors
  );

  useEffect(() => {
    setStorage(state);
  }, [state]);

  useEffect(() => {
		GtmService.bookingAppointmentsScreenShown(
			state.specialization.name,
			state.specialization.id,
			state.service.bookingName || state.service.name,
			state.service.id,
			state.location?.name || "",
			state.location?.id || "",
			state.doctor?.name || "",
			state.doctor?.id || "",
		  );
	}, []);

  function secondStepSubmit() {
    GtmService.bookingSubmitSecondStep(
      state.specialization.name,
      state.specialization.id,
      state.service.bookingName,
      state.service.id,
      state.location.name,
      state.location.id,
      state.doctor.name,
      state.doctor.id,
      state.appointment.startTime
    );

    if (
      state.appointment.appointmentBookingSystem ===
      AppointmentBookingSystem.Helseboka
    ) {
      setIsHelsebokaModalOpen(true);
      return;
    }

    return onSubmit(nextStep, errors);
  }

  function handleMonthChange(newMonth: Date) {
    setAutoQueryRepeat(0);
    setSelectedMonth(newMonth);
  }

  function autoQueryNextMonth() {
    setAutoQueryRepeat(autoQueryRepeat + 1);
    setSelectedMonth(addMonths(selectedMonth, 1));
  }

  function handleDayChange(day: Date) {
    if (day.getTime() == selectedDay?.getTime()) {
      return;
    }

    setSelectedDay(day);
    clear(FormFields.appointment, dispatch, setValue);

    dispatch({
      type: FormReducer.update,
      field: FormFields.selectedDay,
      payload: formatISO(day),
    });
  }

  const onDoctorSelect = (doctor: DoctorDto) => {
    updateMonthToCurrentMonth();
    updateDayToFirstAvailable();
    setValue(FormFields.doctor, doctor);
    dispatch({
      type: FormReducer.update,
      field: FormFields.doctor,
      payload: doctor,
    });
  };

  const onLocationSelect = (location: LocationDto) => {
    updateMonthToCurrentMonth();
    updateDayToFirstAvailable();
    setValue(FormFields.location, location);
    dispatch({
      type: FormReducer.update,
      field: FormFields.location,
      payload: location,
    });
  };

  const updateMonthToCurrentMonth = () =>
    handleMonthChange(startOfMonth(newDateInOsloTimeZone()));

  const updateDayToFirstAvailable = () => {
    if (interval.length) {
      const index = interval.findIndex(
        (day) => day.hasAppointments && day.date >= selectedMonth
      );
      if (index !== -1) {
        setSelectedDay(interval[index].date);
      }
    }
  };

  function handleTimeChoose(appointment: AppointmentDto) {
    setValue(FormFields.appointment, appointment);
    dispatch({
      type: FormReducer.update,
      field: FormFields.appointment,
      payload: appointment,
    });
  }

  usePricingSummary(state.appointment, dispatch);

  useCancellationDeadline(state.appointment, dispatch);

  useEffect(() => {
    updateDayToFirstAvailable();
  }, [interval.length]);

  const shouldShowMonthBackButton = () => {
    if (interval.length == 0) {
      return false;
    }
    const [first] = interval;
    const start = startOfMonth(first.date);
    return start.getTime() < selectedMonth.getTime();
  };

  const isLoading = loadingAppointments || loadingAppointmentsDetails;
  const hasError = !!appointmentsError || !!appointmentsDetailsError;

  const isFailedCenterSelected = appointmentsFailedCenters.some(
    (c) => c.id === state.location?.id
  );

  const warningCenters = !!appointmentsDetailsFailedCenters.length
    ? appointmentsDetailsFailedCenters
    : appointmentsFailedCenters;
  const selectedCentersIds =
    state.location.id && state.location.id != "-1"
      ? state.location.isLocation == true
        ? state.location.centers
        : [state.location.id]
      : [];

  const selectedWarningCenters = !!selectedCentersIds.length
    ? warningCenters.filter((c) => selectedCentersIds.indexOf(c.id) != -1)
    : warningCenters;
  const hasWarning =
    (!!appointmentsWarning || !!appointmentsDetailsWarning) &&
    !!selectedWarningCenters.length;

  const warningMessage = (
    appointmentsWarning || appointmentsDetailsWarning
  )?.replace("$centers", selectedWarningCenters.map((c) => c.name).join(", "));

  const areAppointmentsLoaded =
    interval.length > 0 && filteredAppointmentsDetails.length > 0;

  const { showTimeZoneModal, setShowTimezoneModal } = useTimeZoneModal(
    state,
    dispatch,
    areAppointmentsLoaded,
    hasError
  );

  const submitButtonLabel = secondStepLabels?.nextButtonLabel ?? "Neste";

  function* countdown(a: number, b: number = 1) {
    while (a >= b) {
      yield a--;
    }
  }

  return (
    <div className="mb-3">
      <Heading>{secondStepLabels.subHeading}</Heading>
      <form
        onSubmit={handleSubmit(secondStepSubmit)}
        className="d-flex flex-column"
      >
        <LocationSelect
          defaultValue={state.location}
          label={commonLabels.placeDropdownLabel}
          placeholder={commonLabels.placeDropdownPlaceholder}
          onSelect={onLocationSelect}
          locations={{ items: filteredLocations }}
          unavailableLocations={{ items: unavailableLocations }}
          doctor={state.doctor}
          onDoctorSelect={onDoctorSelect}
          service={state.service}
          specialization={state.specialization}
          additionalLabels={commonLabels}
          status={
            isLoading || hasError ? FieldStatus.disabled : FieldStatus.active
          }
        />
        {state.service.disableDoctorSelection == false &&
          state.location.servicesWithDisabledDoctorSelection.indexOf(
            state.service.id
          ) == -1 && (
            <DoctorSelect
              defaultValue={state.doctor}
              label={secondStepLabels.selectDoctorDropdownLabel}
              labelSelected={secondStepLabels.selectDoctorDropdownLabelSelected}
              placeholder={secondStepLabels.selectDoctorDropdownPlaceholder}
              onSelect={onDoctorSelect}
              doctors={filteredDoctors}
              doctor={state.doctor}
              status={
                isLoading || hasError
                  ? FieldStatus.disabled
                  : FieldStatus.active
              }
            />
          )}
        {state.doctor.id && state.doctor.id != "-1" && (
          <div className="row mt-2 mb-3">
            <DoctorCard doctor={state.doctor} centers={rootData.centers} />
          </div>
        )}
        {state.relatedServiceSetups.map((rs) => {
          const relatedServiceLocationSetup = findRelatedServiceLocationSetup(
            rs,
            state.location
          );

          return (
            isRelatedServiceEnabled(rs, state.location) && (
              <div
                className="booking-narrow-item mb-2 position-relative"
                key={rs.relatedServiceId}
              >
                <CheckBox
                  name={`${FormFields.showRelatedServices}-${rs.relatedServiceId}`}
                  label={
                    relatedServiceLocationSetup?.showRelatedServicesCheckboxLabel ??
                    rs.showRelatedServicesCheckboxLabel
                  }
                  heading={
                    relatedServiceLocationSetup?.showRelatedServicesLabel ??
                    rs.showRelatedServicesLabel
                  }
                  register={register}
                  onClick={() =>
                    changeShowRelatedSetup(
                      !state.showRelatedServicesMap.get(rs.relatedServiceId),
                      rs.relatedServiceId
                    )
                  }
                  backgroundColor={rs.highlightColor}
                  disabled={hasError || isLoading}
                />
              </div>
            )
          );
        })}

        {!!noAppointmentMessage ? (
          <RichTextArea
            content={noAppointmentMessage}
            innerClassName="mt-3"
          />
        ) : state.service.availableForOnlineBooking ? (
          <>
            {hasError && (
              <ErrorMessage
                message={appointmentsError || appointmentsDetailsError}
              />
            )}
            {hasWarning && !hasError && (
              <ErrorMessage message={warningMessage} />
            )}
            {loadingAppointments && !hasError && !isFailedCenterSelected && (
              <LoadingAnimation
                text={
                  autoQueryRepeat === 0
                    ? bookingLabels!.stepsBookingLabels.secondStepLabels.loadingAppointmentsForCurrentMonth.replace(
                        "$currentMonth",
                        format(selectedMonth, "MMMM", { locale: nb })
                      )
                    : bookingLabels!.stepsBookingLabels.secondStepLabels.loadingAppointmentsForNextMonth
                        .replace(
                          "$currentMonth",
                          format(selectedMonth, "MMMM", { locale: nb })
                        )
                        .replace(
                          "$checkedMonths",
                          [...countdown(autoQueryRepeat)]
                            .map((value) =>
                              format(subMonths(selectedMonth, value), "MMMM", {
                                locale: nb,
                              })
                            )
                            .join(", ")
                        )
                }
                className="loading-animation-container text-center"
              />
            )}

            {!loadingAppointments && !hasError && !isFailedCenterSelected && (
              <>
                <MonthPicker
                  currentMonth={selectedMonth}
                  handleButtonClick={handleMonthChange}
                  displayBackButton={shouldShowMonthBackButton()}
                />
                {interval.length === 0 ? (
                  <RichTextArea
                    content={
                      autoQueryRepeat === 0
                        ? bookingLabels!.stepsBookingLabels.secondStepLabels
                            .noAppointments
                        : bookingLabels!.stepsBookingLabels.secondStepLabels
                            .noAppointmentsAutoQuery
                    }
                  />
                ) : (
                  <>
                    <DayPicker
                      selectedDay={selectedDay}
                      selectedMonth={selectedMonth}
                      handleDayChange={handleDayChange}
                      handleMonthChange={handleMonthChange}
                      interval={interval}
                      currentSlideIndex={currentSlideIndex}
                      setCurrentSlideIndex={setCurrentSlideIndex}
                    />
                    {selectedDay && (
                      <HourPicker
                        availableAppointments={filteredAppointmentsDetails}
                        selectedAppointment={state.appointment}
                        selectedDay={selectedDay!}
                        loading={loadingAppointmentsDetails}
                        handleTimeChange={handleTimeChoose}
                        doctors={rootData.doctors}
                        centers={rootData.centers}
                        availableDays={filteredAppointments.map((a) => a.date)}
                        goToNextAvailableDay={handleDayChange}
                        selectedDoctor={state.doctor}
                        selectedLocation={state.location}
                        selectedService={state.service}
                        relatedServiceSetups={state.relatedServiceSetups}
                      />
                    )}
                  </>
                )}
              </>
            )}
            {isHelsebokaModalOpen && (
              <GenericModal
                onClose={() => setIsHelsebokaModalOpen(false)}
                onSubmit={submitHelsebokaModal}
                isOpen={isHelsebokaModalOpen}
                modalHeader={
                  !!secondStepLabels?.helsebokaRedirectModalHeading
                    ? secondStepLabels.helsebokaRedirectModalHeading
                    : "Fullfør din bestilling på Helseboka"
                }
                modalDescription={
                  !!secondStepLabels?.helsebokaRedirectModalIntro
                    ? secondStepLabels.helsebokaRedirectModalIntro
                    : "Nettbestilling til Sandvika Nevrosenter gjøres med BankID på helseboka.app"
                }
                submitButtonLabel={
                  !!secondStepLabels?.helsebokaRedirectModalButton
                    ? secondStepLabels.helsebokaRedirectModalButton
                    : "Fortsett til Helseboka"
                }
              />
            )}
            <Submit
              label={secondStepLabels?.nextButtonLabel}
              status={
                state.appointment.serviceId
                  ? FieldStatus.active
                  : FieldStatus.disabled
              }
            >
              {isLoading ? (
                <LoadingAnimation text={submitButtonLabel} />
              ) : (
                submitButtonLabel
              )}
            </Submit>
          </>
        ) : (
          <div
            dangerouslySetInnerHTML={{
              __html: noAppointmentMessage,
            }}
          />
        )}
      </form>
      <GenericModal
        onClose={() => {
          setShowTimezoneModal(false);
        }}
        isOpen={showTimeZoneModal}
        modalHeader={secondStepLabels.timeZoneModalHeader}
        modalDescription={secondStepLabels.timeZoneModalDescription}
        submitButtonLabel={secondStepLabels.timeZoneModalButtonLabel}
      />
    </div>
  );
};
